import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import {
  firstValueFrom,
  from,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import { Store } from '@ngxs/store';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatOption, MatSelect } from '@angular/material/select';
import {
  normalizeManyRelations,
  normalizeOneRelation,
} from '../../core/decorators/relation.decorator';
import { map } from 'rxjs/operators';
import { MultiComboEntitySearchComponent } from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MatDivider } from '@angular/material/divider';
import { LearningUnitEntity } from '../../learning-unit/learning-unit-entity';
import { StateFilterCBComponent } from '../../utils/component/state-filter-combobox/state-cb.component';
import { EntityStateModel } from '../../core/state/entity.state';
import { MatIcon } from '@angular/material/icon';
import { MediaType } from '../../core/types';
import { ChooseMedium } from '../../utils/component/choose-medium/choose-medium.json.model';
import { ChooseMediumComponent } from '../../utils/component/choose-medium/choose-medium.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../environments/environment';
import { ConfirmDialogComponent } from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { LearningSectionEntity } from '../../learning-section/learning-section-entity';
import { ModuleEntityService } from '../../module/services/module.entity.service';
import { LearningSectionEntityService } from '../../learning-section/services/learning-section.entity.service';
import { ModuleEntity } from '../../module/module.entity';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import {
  ConsumerEntityService,
  ConsumerEntityState,
} from '../../consumer/services/consumer.entity.service';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ObjectConsumerMetadataComponent } from '../../consumer/object-consumer-metadata/object-consumer-metadata.component';
import { RelationRef } from '../../core/types/entity-types';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { PixlEditorComponent } from '@wdidev/pixl-editor';
import { AuthState } from '../../core/authentication/auth.state';
import { AssetEntity } from '../../asset/asset.entity';

@Component({
  selector: 'pixl-form-dialog-learning-unit',
  templateUrl: './form-dialog-learning-unit.component.html',
  imports: [
    CommonModule,
    MatDialogContent,
    MatFormField,
    MatDialogTitle,
    MatLabel,
    MatFormField,
    MatInput,
    MatFormField,
    MatDialogActions,
    ReactiveFormsModule,
    MatButton,
    MatSelect,
    MatOption,
    FormsModule,
    MultiComboEntitySearchComponent,
    CdkDrag,
    CdkDropList,
    MatDivider,
    StateFilterCBComponent,
    MatIcon,
    MatIconButton,
    AsyncPipe,
    MatTabGroup,
    MatTab,
    ObjectConsumerMetadataComponent,
    TranslocoModule,
    PixlEditorComponent,
  ],
})
export class FormDialogLearningUnitComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<FormDialogLearningUnitComponent>>(MatDialogRef);

  data? = inject<Partial<LearningUnitEntity>>(MAT_DIALOG_DATA);

  form: FormGroup;
  consumers$!: Observable<ConsumerEntity[]>;
  learningUnits$!: Observable<LearningUnitEntity[]>;
  modules$!: Observable<ModuleEntity[]>;
  learningSections$!: Observable<LearningSectionEntity[]>;
  selectedConsumerName$!: Observable<string>;

  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private consumerService = inject(ConsumerEntityService);
  private moduleService = inject(ModuleEntityService);
  private learningSectionService = inject(LearningSectionEntityService);

  selectedModules: ModuleEntity[] = [];
  currentPageModules = 1;
  pageSizeModules = 20;
  searchQueryModules: string | null = null;

  selectedLearningSections: LearningSectionEntity[] = [];
  currentPageLearningSections = 1;
  pageSizeLearningSections = 20;
  searchQueryLearningSections: string | null = null;

  public imageUrl = signal('');

  public auth$ = this.store.select(AuthState.getToken);
  public authToken: string | null = '';
  public assetsUrl: string = environment.apiUrl + '/assets';
  public mediaUrl: string = environment.assetsHost + '/';
  public descriptionHtml = '';

  status = '';

  constructor() {
    const data = this.data;

    this.form = this.fb.group({
      title: [data?.title || ''],
      subtitle: [data?.subtitle || ''],
      description: [data?.description || ''],
      status: [data?.status || 'Edit'],
      preview: [data?.preview || []],
      lesson_section: [data?.lesson_section || ''],
      processing_time: [data?.processing_time || ''],
      // federal_state: [data?.federal_state || []],
      learning_unit_goal: [data?.learning_unit_goal || ''],
      learning_unit_type: [data?.learning_unit_type || ''],
      changed_by: [data?.changed_by || ''],
      consumer: [
        normalizeOneRelation<ConsumerEntity>(
          data?.consumer as RelationRef<ConsumerEntity>[],
        ),
      ],
      consumerData: this.fb.group(
        Object.fromEntries(
          Object.entries(data?.consumerData || {}).map(([key, value]) => [
            key,
            Array.isArray(value) ? [value] : value,
          ]),
        ),
      ),
      modules: [normalizeManyRelations(data?.modules)],
      learningSections: [normalizeManyRelations(data?.learningSections)],
    });
    this.buildURLs();
  }

  ngOnInit() {
    this.auth$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((token) => {
      this.authToken = token;
    });

    this.descriptionHtml = this.data?.description || '';

    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery),
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery)),
      ),
    );

    this.selectedConsumerName$ = this.form.get('consumer')!.valueChanges.pipe(
      startWith(this.form.get('consumer')!.value),
      switchMap((val) => {
        const id = Array.isArray(val) ? val[0] : val;
        if (!id) {
          return of('Metadaten');
        }
        return this.store.select(ConsumerEntityState.byId).pipe(
          map((fn) => fn(id)),
          map((consumer) => consumer?.name || 'Metadaten'),
        );
      }),
    );

    const moduleQuery = {
      'page[size]': this.pageSizeModules,
      'page[number]': this.currentPageModules,
      'sort': 'title',
    };

    this.modules$ = from(this.moduleService.ensureLoaded(moduleQuery)).pipe(
      switchMap(() =>
        this.store.select(this.moduleService.allByQuery(moduleQuery)).pipe(
          map((paginated) => {
            const combined = [...this.selectedModules, ...paginated].filter(
              (lu, index, self) =>
                index === self.findIndex((s) => s.id === lu.id),
            );
            return combined;
          }),
        ),
      ),
    );

    const selectedModuleIds = normalizeManyRelations(
      this.data?.modules,
    ) as string[];

    const moduleState = this.store.selectSnapshot(
      this.moduleService.stateClass,
    ) as EntityStateModel<ModuleEntity>;
    this.selectedModules = selectedModuleIds
      .map((id) => moduleState.entities[id])
      .filter((e): e is ModuleEntity => !!e);

    this.modules$ = this.store
      .select(this.moduleService.allByQuery(moduleQuery))
      .pipe(
        map((paginated) => {
          const combined = [...this.selectedModules, ...paginated].filter(
            (seq, index, self) =>
              index === self.findIndex((s) => s.id === seq.id),
          );
          return combined;
        }),
      );

    this.form.get('modules')?.setValue(this.selectedModules.map((s) => s.id));

    const learningSectionQuery = {
      'page[size]': this.pageSizeLearningSections,
      'page[number]': this.currentPageLearningSections,
      'sort': 'name',
    };

    this.learningSections$ = from(
      this.learningSectionService.ensureLoaded(learningSectionQuery),
    ).pipe(
      switchMap(() =>
        this.store
          .select(this.learningSectionService.allByQuery(learningSectionQuery))
          .pipe(
            map((paginated) => {
              const combined = [
                ...this.selectedLearningSections,
                ...paginated,
              ].filter(
                (lu, index, self) =>
                  index === self.findIndex((s) => s.id === lu.id),
              );
              return combined;
            }),
          ),
      ),
    );

    const selectedLearningSectionIds = normalizeManyRelations(
      this.data?.learningSections,
    ) as string[];

    const learningSectionState = this.store.selectSnapshot(
      this.learningSectionService.stateClass,
    ) as EntityStateModel<LearningSectionEntity>;
    this.selectedLearningSections = selectedLearningSectionIds
      .map((id) => learningSectionState.entities[id])
      .filter((e): e is LearningSectionEntity => !!e);

    this.learningSections$ = this.store
      .select(this.learningSectionService.allByQuery(learningSectionQuery))
      .pipe(
        map((paginated) => {
          const combined = [
            ...this.selectedLearningSections,
            ...paginated,
          ].filter(
            (seq, index, self) =>
              index === self.findIndex((s) => s.id === seq.id),
          );
          return combined;
        }),
      );

    this.form
      .get('learningSections')
      ?.setValue(this.selectedLearningSections.map((s) => s.id));
  }

  receiveModuleData(selected: ModuleEntity[] | null) {
    this.selectedModules = selected ?? [];
    this.form.get('modules')?.setValue(this.selectedModules.map((s) => s.id));
  }

  async receiveModuleQuery(query: string) {
    this.searchQueryModules = query?.trim() || null;

    if (this.searchQueryModules && this.searchQueryModules.length > 3) {
      // search mode
      const searchParams = { 'filter[titleLike]': this.searchQueryModules };
      await this.moduleService.ensureLoaded(searchParams);

      this.modules$ = this.store
        .select(this.moduleService.allByQuery(searchParams))
        .pipe(map((results) => results));
    }
    else {
      // default paginated mode
      const pageQuery = {
        'page[size]': this.pageSizeModules,
        'page[number]': this.currentPageModules,
        'sort': 'title',
      };
      this.modules$ = this.store
        .select(this.moduleService.allByQuery(pageQuery))
        .pipe(
          map((paginated) => {
            const combined = [...this.selectedModules, ...paginated].filter(
              (lu, index, self) =>
                index === self.findIndex((s) => s.id === lu.id),
            );
            return combined;
          }),
        );
    }
  }

  async loadMoreModules(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryModules) {
      const pageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'title',
      };

      await this.moduleService.ensureLoaded(pageQuery);

      const currentModules = await firstValueFrom(this.modules$ ?? of([]));

      this.modules$ = this.store
        .select(this.moduleService.allByQuery(pageQuery))
        .pipe(
          map((paginated) => {
            const combined = [
              ...this.selectedModules,
              ...currentModules,
              ...paginated,
            ].filter(
              (lu, index, self) =>
                index === self.findIndex((s) => s.id === lu.id),
            );
            return combined;
          }),
        );
    }
  }

  get moduleLabel(): string {
    if (!this.selectedModules || !this.selectedModules.length) {
      return 'Module';
    }
    return this.selectedModules.map((module) => module.title).join(', ');
  }

  receiveLearningSectionData(selected: LearningSectionEntity[] | null) {
    this.selectedLearningSections = selected ?? [];
    this.form
      .get('learningSections')
      ?.setValue(this.selectedLearningSections.map((s) => s.id));
  }

  async receiveLearningSectionQuery(query: string) {
    this.searchQueryLearningSections = query?.trim() || null;

    if (
      this.searchQueryLearningSections &&
      this.searchQueryLearningSections.length > 3
    ) {
      // search mode
      const searchParams = {
        'filter[like]': JSON.stringify({
          term: this.searchQueryLearningSections,
          columns: ['name', 'learning_section_type'],
        }),
      };
      await this.learningSectionService.ensureLoaded(searchParams);

      this.learningSections$ = this.store
        .select(this.learningSectionService.allByQuery(searchParams))
        .pipe(map((results) => results));
    }
    else {
      // default paginated mode
      const pageQuery = {
        'page[size]': this.pageSizeLearningSections,
        'page[number]': this.currentPageLearningSections,
        'sort': 'name',
      };
      this.learningSections$ = this.store
        .select(this.learningSectionService.allByQuery(pageQuery))
        .pipe(
          map((paginated) => {
            const combined = [...this.selectedModules, ...paginated].filter(
              (lu, index, self) =>
                index === self.findIndex((s) => s.id === lu.id),
            );
            return combined;
          }),
        );
    }
  }

  async loadMoreLearningSections(event: {
    pageNumber: number
    pageSize: number
  }) {
    if (!this.searchQueryLearningSections) {
      const pageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'name',
      };

      await this.learningSectionService.ensureLoaded(pageQuery);

      const currentLearningSections = await firstValueFrom(
        this.learningSections$ ?? of([]),
      );

      this.learningSections$ = this.store
        .select(this.learningSectionService.allByQuery(pageQuery))
        .pipe(
          map((paginated) => {
            const combined = [
              ...this.selectedLearningSections,
              ...currentLearningSections,
              ...paginated,
            ].filter(
              (lu, index, self) =>
                index === self.findIndex((s) => s.id === lu.id),
            );
            return combined;
          }),
        );
    }
  }

  reOrderSelectedLearningSections(event: CdkDragDrop<LearningSectionEntity[]>) {
    moveItemInArray(
      this.selectedLearningSections,
      event.previousIndex,
      event.currentIndex,
    );
    this.form
      .get('learningSections')
      ?.setValue(this.selectedLearningSections.map((lu) => lu.id));
  }

  private translocoService = inject(TranslocoService);

  public get learningSectionLabel(): string {
    if (
      !this.selectedLearningSections ||
      !this.selectedLearningSections.length
    ) {
      return this.translocoService.translate('learning_sections_name_or_type');
    }
    return this.selectedLearningSections
      .map((learningSection) => learningSection.name)
      .join(', ');
  }

  public openImageChoice(): void {
    this.showMediumDialog('Image');
  }

  public showMediumDialog(mediaType: MediaType): void {
    const chooseMedium: ChooseMedium = {
      mediaType: mediaType,
      desktopAudio: false,
      subjectId: '',
    };

    const dialogRef = this.dialog.open(ChooseMediumComponent, {
      minWidth: '60vw',
      maxWidth: '80vw',
      minHeight: '530px',
      maxHeight: '530px',
      data: chooseMedium,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answer: AssetEntity) => {
        if (!answer) return;

        const previewControl = this.form.get('preview');

        if (!previewControl?.value || Array.isArray(previewControl.value)) {
          // initialize as object if empty
          previewControl?.setValue({
            id: '',
            name: '',
            mediaType: '',
            media_url: '',
          });
        }

        // update the form control value
        previewControl?.patchValue({
          id: answer.id,
          mediaType: 'Image',
          media_url: answer.media_url,
          name: answer.name,
        });

        this.cdr.detectChanges();
        this.buildURLs();
      });
  }

  private buildURLs(): void {
    const baseUrl = environment.isProd ?
      '/' :
      'https://zav-stage.westermann.de/';

    const preview = this.form.get('preview')?.value;

    if (preview && preview.media_url) {
      this.imageUrl.set(baseUrl + preview.media_url);
    }
  }

  public deleteMediaEntry(): void {
    const previewControl = this.form.get('preview');
    const preview = previewControl?.value;

    const id = preview?.id;
    const name = preview?.name;
    const headerText = 'Bild-Datei löschen';

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        id,
        name,
        itemType: 'Datei',
        headerText,
        deleteFn: () => {
          // clear the form control instead of this.data.preview
          previewControl?.setValue({
            id: '',
            name: '',
            mediaType: '',
            media_url: '',
          });
          this.cdr.detectChanges();
          dialogRef.close();
        },
      },
    });
  }

  public getImageName(): string {
    const preview = this.form.get('preview')?.value;
    return preview?.name || '';
  }

  statusChanged(newStatus: string) {
    this.status = newStatus;
    this.form.get('status')?.setValue(newStatus);
  }

  hasPixlEditorDescription(): boolean {
    const consumerData = this.form.get('consumerData')?.value;
    return consumerData?.pixl_editor_description === true;
  }

  save(): void {
    if (this.hasPixlEditorDescription()) {
      this.form.get('description')?.setValue(this.descriptionHtml);
    }

    if (this.form.valid) {
      const formValue = this.form.value;

      const updatedLearningUnit = Object.assign(
        new LearningUnitEntity(),
        { id: this.data?.id },
        formValue,
        {
          consumer: formValue.consumer ?
            { type: 'consumers', id: formValue.consumer } :
            null,
          modules: (formValue.modules as string[])?.length ?
            (formValue.modules as string[]).map((id) => ({ id })) :
            [],
          learningSections: (formValue.learningSections as string[])?.length ?
            (formValue.learningSections as string[]).map((id) => ({ id })) :
            [],
        },
      );

      this.dialogRef.close(updatedLearningUnit);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  trackLearningSectionsById(index: number, item: LearningSectionEntity) {
    return item.id;
  }

  trackById(index: number, item: ModuleEntity) {
    return item.id;
  }
}
